import { BattleManager, BattleManagerOptions } from "../../game/assets/pri_wqs/scripts/core/BattleManager";
import type {
  BattleRuntimeState,
  PropRuntimeEvent,
  RuntimePropInstance,
} from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import { getRuntimeYield, isEmptyPropInstance } from "../../game/assets/pri_wqs/scripts/core/propMechanics/runtimeNodes/RuntimeNodeUtils";
import type { PropRuntimeGraph } from "../../game/assets/shared/types/PropRuntimeGraph";
import { dataCall } from "../../../tsrpc";
import type { SandboxEventLog } from "./types";

export const SANDBOX_PROP_TYPE_LABELS: Record<number, string> = {
  1: "原料",
  2: "辅料",
  3: "配方",
};

export const SANDBOX_RARITY_LABELS: Record<number, string> = {
  1: "普通",
  2: "少见",
  3: "稀有",
  4: "罕见",
};

export const SANDBOX_TAG_LABELS: Record<number, string> = {
  1: "植物",
  2: "昆虫",
  3: "矿物",
  4: "蘑菇",
  5: "羽毛",
  6: "液体",
  7: "工艺",
  8: "动物",
  99: "杂物",
};

const SANDBOX_EVENT_LABELS: Record<PropRuntimeEvent["type"], string> = {
  gameStarted: "游戏开始",
  gameEnded: "游戏结束",
  commissionStarted: "委托开始",
  commissionEnded: "委托结束",
  roundStarted: "回合开始",
  roundEnded: "回合结束",
  propAcquired: "道具入手",
  propRemoved: "道具移除",
  propEnteredBoard: "进入盘面",
  propLeftBoard: "离开盘面",
  propTimerChanged: "计时器变化",
  propEliminated: "道具消除",
  propTransformed: "道具转化",
  propPositionSwapped: "位置交换",
  propUsed: "主动使用",
  propCollected: "魔力收集",
  magicChanged: "魔力变化",
  rarityWeightChanged: "品质权重变化",
  permanentBoostRulesChanged: "永久加成变化",
  commissionRoundLimitChanged: "调和轮数变化",
  animationTriggered: "动画触发",
};

export async function loadPropRuntimeGraphs(): Promise<PropRuntimeGraph[]> {
  const res = await dataCall("editor/DataFind", {
    collection: "propruntimegraphs",
    filter: {},
    sort: { assetId: 1, name: 1 },
    limit: 10000,
  });
  if (!res.isSucc) throw new Error(res.err.message);
  return (res.res.data ?? []).map(normalizeRuntimeGraph);
}

export function createSandboxBattleManager(
  catalog: PropRuntimeGraph[],
  options: BattleManagerOptions,
): BattleManager {
  BattleManager.disposeInstance();
  return BattleManager.getInstance(catalog, options);
}

export function disposeSandboxBattleManager(): void {
  BattleManager.disposeInstance();
}

export function getSandboxPropTypeLabel(type: number): string {
  return SANDBOX_PROP_TYPE_LABELS[type] ?? `类型${type}`;
}

export function getSandboxRarityLabel(rarity: number): string {
  return SANDBOX_RARITY_LABELS[rarity] ?? `品质${rarity}`;
}

export function getSandboxTagLabel(tag: number): string {
  return SANDBOX_TAG_LABELS[tag] ?? `标签${tag}`;
}

export function getSandboxEventLabel(type: PropRuntimeEvent["type"]): string {
  return SANDBOX_EVENT_LABELS[type] ?? "未知事件";
}

export function getRuntimeInstanceName(item?: RuntimePropInstance): string {
  if (!item) return "-";
  const suffix = item.alive ? "" : "（失效）";
  return `${item.graph.name || item.graph._id}${suffix}`;
}

export function getRuntimeGraphSummary(graph: PropRuntimeGraph): string {
  const tags = (graph.tags ?? []).map(getSandboxTagLabel).join("、") || "无标签";
  return `${getSandboxPropTypeLabel(graph.type)} / ${getSandboxRarityLabel(graph.rarity)} / ${tags}`;
}

export function isRuntimeEmptySlot(item?: RuntimePropInstance): boolean {
  if (!item) return false;
  return isEmptyPropInstance(item) || item.graph.name === "空位置";
}

export function readRuntimeYield(item?: RuntimePropInstance): number {
  if (!item || isRuntimeEmptySlot(item)) return 0;
  return getRuntimeYield(item);
}

export function readRuntimeItem(state: BattleRuntimeState | undefined, id: string): RuntimePropInstance | undefined {
  return state?.items.find((item) => item.id === id);
}

export function readRuntimeItems(state: BattleRuntimeState | undefined, ids: string[]): RuntimePropInstance[] {
  return ids
    .map((id) => readRuntimeItem(state, id))
    .filter((item): item is RuntimePropInstance => Boolean(item));
}

export function readBoardMatrix(state: BattleRuntimeState | undefined): Array<Array<RuntimePropInstance | undefined>> {
  if (!state) return [];
  return state.board.map((row) => row.map((id) => readRuntimeItem(state, id)));
}

export function createSandboxEventLog(event: PropRuntimeEvent, state: BattleRuntimeState): SandboxEventLog {
  const itemName = event.item ? getRuntimeInstanceName(event.item) : undefined;
  const targetName = event.target ? getRuntimeInstanceName(event.target) : undefined;
  const sourceName = event.source ? getRuntimeInstanceName(event.source) : undefined;
  return {
    id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    totalRoundIndex: state.totalRoundIndex,
    commissionIndex: state.commissionIndex,
    roundIndex: state.roundIndex,
    type: event.type,
    itemName,
    targetName,
    sourceName,
    magicDelta: event.magicDelta,
    summary: summarizeRuntimeEvent(event, itemName, targetName, sourceName),
    event,
  };
}

export function summarizeRuntimeEvent(
  event: PropRuntimeEvent,
  itemName?: string,
  targetName?: string,
  sourceName?: string,
): string {
  const parts: string[] = [];
  if (itemName) parts.push(`道具：${itemName}`);
  if (targetName && targetName !== itemName) parts.push(`目标：${targetName}`);
  if (sourceName && sourceName !== itemName) parts.push(`来源：${sourceName}`);
  if (event.prototype?.name) parts.push(`原型：${event.prototype.name}`);
  if (event.prototypeId) parts.push(`原型ID：${event.prototypeId}`);
  if (typeof event.magicDelta === "number") parts.push(`魔力：${formatSignedNumber(event.magicDelta)}`);
  return parts.length ? `${getSandboxEventLabel(event.type)}：${parts.join("，")}` : getSandboxEventLabel(event.type);
}

export function serializeSandboxState(state: BattleRuntimeState | undefined): string {
  if (!state) return "{}";
  const snapshot = {
    rows: state.rows,
    cols: state.cols,
    commissionIndex: state.commissionIndex,
    roundIndex: state.roundIndex,
    totalRoundIndex: state.totalRoundIndex,
    currentRoundMagic: state.currentRoundMagic,
    totalMagic: state.totalMagic,
    materialLibrary: readRuntimeItems(state, state.materialLibrary).map(serializeRuntimeInstance),
    recipes: readRuntimeItems(state, state.recipes).map(serializeRuntimeInstance),
    consumables: readRuntimeItems(state, state.consumables).map(serializeRuntimeInstance),
    board: readBoardMatrix(state).map((row) => row.map((item) => item ? serializeRuntimeInstance(item) : null)),
    rarityWeightMultipliers: state.rarityWeightMultipliers,
    rarityWeightRules: state.rarityWeightRules.map((rule) => ({
      id: rule.id,
      sourceId: rule.sourceId,
      actionNodeId: rule.actionNodeId,
      rarity: rule.rarity,
      multiplier: rule.multiplier,
      persistent: rule.persistent,
    })),
    permanentBoostRules: state.permanentBoostRules.map((rule) => ({
      id: rule.id,
      sourceId: rule.sourceId,
      sourceGraphId: rule.sourceGraphId,
      actionNodeId: rule.actionNodeId,
      keepAfterSourceRemoved: rule.keepAfterSourceRemoved,
    })),
    roundYieldBoosts: state.roundYieldBoosts,
  };
  return JSON.stringify(snapshot, null, 2);
}

export function serializeRuntimeInstance(item: RuntimePropInstance) {
  return {
    id: item.id,
    prototypeId: item.graph._id,
    name: item.graph.name,
    type: item.graph.type,
    rarity: item.graph.rarity,
    tags: item.graph.tags,
    row: item.row,
    col: item.col,
    alive: item.alive,
    baseYield: item.graph.yield,
    runtimeYield: readRuntimeYield(item),
    grow: item.grow,
    protectStacks: item.protectStacks,
    purified: item.purified,
    collectedMagic: item.collectedMagic,
    useCount: item.useCount,
    meta: item.meta,
    negativeEffects: item.negativeEffects,
  };
}

export function formatSignedNumber(value: number): string {
  if (value > 0) return `+${value}`;
  return String(value);
}

function normalizeRuntimeGraph(raw: any): PropRuntimeGraph {
  return {
    ...raw,
    _id: String(raw?._id ?? ""),
    tags: Array.isArray(raw?.tags) ? raw.tags.map((tag: unknown) => Number(tag)) : [],
    type: Number(raw?.type ?? 0),
    rarity: Number(raw?.rarity ?? 0),
    yield: Number(raw?.yield ?? 0),
    derived: Boolean(raw?.derived),
  };
}
